import { supabase } from '@/integrations/supabase/client';

export interface LocationConfig {
  value: string;
  name: string;
  address: string;
  /** Care type — DB CHECK constraint on locations.type guarantees these values */
  type: 'kdv' | 'bso' | 'mixed' | null;
  tourBookingHorizonDays: number;
  toursEnabled: boolean;
  /**
   * Curated Google Maps short link (maps.app.goo.gl) pinning to the exact building.
   * NULL = the UI must hide the map link entirely. Do NOT fall back to a
   * search-query URL from `address` — that's what we just removed.
   */
  googleMapsUrl: string | null;
}

/**
 * Fetch all locations from the database
 * Throws if fetch fails — no fallbacks, DB is the single source of truth
 */
export async function getLocations(): Promise<LocationConfig[]> {
  const { data, error } = await supabase
    .from('locations')
    .select('code, name, address, type, tour_booking_horizon_days, tours_enabled, google_maps_url')
    .order('code');

  if (error) {
    console.error('[locationsService] Error fetching locations:', error);
    throw new Error(`Failed to fetch locations: ${error.message}`);
  }

  if (!data || data.length === 0) {
    throw new Error('No locations found in database');
  }

  // Map DB data to LocationConfig interface
  return data.map((loc) => ({
    value: loc.code,
    name: loc.name,
    address: loc.address,
    // Generated type is `string | null`; the DB CHECK constraint narrows it.
    type: loc.type as 'kdv' | 'bso' | 'mixed' | null,
    tourBookingHorizonDays: loc.tour_booking_horizon_days ?? 28,
    toursEnabled: loc.tours_enabled ?? false,
    googleMapsUrl: loc.google_maps_url,
  }));
}

/**
 * Get a single location by its code
 * Returns null if not found
 */
export async function getLocationByCode(code: string): Promise<LocationConfig | null> {
  const { data, error } = await supabase
    .from('locations')
    .select('code, name, address, type, tour_booking_horizon_days, tours_enabled, google_maps_url')
    .eq('code', code)
    .single();

  if (error || !data) {
    return null;
  }

  return {
    value: data.code,
    name: data.name,
    address: data.address,
    // Generated type is `string | null`; the DB CHECK constraint narrows it.
    type: data.type as 'kdv' | 'bso' | 'mixed' | null,
    tourBookingHorizonDays: data.tour_booking_horizon_days ?? 28,
    toursEnabled: data.tours_enabled ?? false,
    googleMapsUrl: data.google_maps_url,
  };
}
